import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import Network from '../Network';
import { isNetworkError } from '../utils/getErrorMessage';

interface UseGetOneOptions {
  baseURL?: string;
  page?: number;
  pageSize?: number;
  additionalParams?: Record<string, any>;
  enabled?: boolean;
  skipPagination?: boolean;
  useQueryOptions?: Omit<
    UseQueryOptions<any, unknown, any, any[]>,
    'queryKey' | 'queryFn' | 'enabled'
  >;
}

export const useGetOne = (
  queryKey: string,
  id: string | number,
  options: UseGetOneOptions = {}
) => {
  const {
    baseURL = '',
    page = 1,
    pageSize = 10,
    additionalParams = {},
    enabled = true,
    skipPagination = false,
    useQueryOptions = {},
  } = options;

  const buildQueryString = () => {
    if (skipPagination) {
      return '';
    }

    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', pageSize.toString());

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    return params.toString();
  };

  return useQuery({
    queryKey: [queryKey, id, page, pageSize, additionalParams, skipPagination],
    queryFn: async () => {
      if (!id || !baseURL) {
        throw new Error('ID and baseURL are required');
      }

      const queryString = buildQueryString();
      const url = baseURL.includes(`/${id}`)
        ? queryString
          ? `${baseURL}?${queryString}`
          : baseURL
        : queryString
          ? `${baseURL}/${id}?${queryString}`
          : `${baseURL}/${id}`;

      const response = await Network.get(url);
      return response.data;
    },
    enabled: enabled && !!id && !!baseURL,
    retry: (failureCount, error) => isNetworkError(error) && failureCount < 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: false,
    ...useQueryOptions,
  });
};
